import React, { useContext, useEffect, useRef, useState } from "react";

import { FormHelperText, Grid } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { UserContext } from "App";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import Swal from "sweetalert2";

import { GetNewsDetails, EditNewsAPI } from "APIs/NewsAPIs";

import FullScreenLoader from "components/Custom/FullScreenLoader";

import { themedDialog } from "components/Custom/AreYouSurePopUp";

import { notify } from "App";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "edit-news-form";

// Required fields (checked on blur and when Save is clicked)
const FIELDS = [
  { name: "news_title", label: "Title" },
  { name: "news_description", label: "Description" },
];

const getFieldError = ({ name, label }, data) =>
  String(data?.[name] ?? "").trim() === "" ? `${label} is required` : "";

const compactCardsSx = {
  "& > div": {
    padding: { xs: "12px", md: "16px" },
  },
};

const EditNews = () => {
  const navigate = useNavigate();

  const userData = useContext(UserContext);

  const dispatch = useDispatch();

  const { accentName } = useAccent();

  const [newsData, setNewsData] = useState({
    news_title: "",
    news_description: "",
    news_id: "",
  });

  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState(true);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // An error only appears after the field is touched (blurred, or Save was clicked)
  const getVisibleError = (field) => (touched[field.name] ? getFieldError(field, newsData) : "");

  const isFormValid = FIELDS.every((field) => !getFieldError(field, newsData));

  const { id } = useParams();

  const handleNewsDataChange = (e) => {
    setNewsData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getNewsDetailsFunc = async (id, access_token, dispatch) => {
    setIsFetchingData(true);
    const response = await GetNewsDetails(id, access_token, dispatch);

    if (response !== null) {
      const formattedDescription = response.news_description
        ? response.news_description.replace(/\\n/g, "\n")
        : "";

      setNewsData((prev) => ({
        ...prev,
        news_title: response.news_title || "",
        news_description: formattedDescription,
        news_id: response.news_id ? response.news_id.toString() : "",
      }));
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getNewsDetailsFunc(id, userData.access_token, dispatch);
    }
  }, [userData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    // Invalid form: show every error and scroll to the first one
    if (!isFormValid) {
      setTouched(Object.fromEntries(FIELDS.map((field) => [field.name, true])));
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    Swal.fire({
      title: "Save changes?",
      text: "Your changes to this news will be saved.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
      cancelButtonText: "Cancel",
      ...themedDialog,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);

        try {
          const editNews = await EditNewsAPI(newsData, userData.access_token, dispatch);

          if (editNews !== null) {
            navigate("/news");
          }

          notify("News Updated!", "success");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      <SectionHeader
        icon="edit"
        label="EDIT NEWS"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/news")}
          >
            Back
          </MDButton>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
            <MDBox sx={compactCardsSx}>
              <SectionCard title="Details" subtitle="Fields marked * are required">
                <Grid container spacing={2}>
                  {/* Title Input */}
                  <FormField name="title" required full>
                    <MDInput
                      name="news_title"
                      type="text"
                      placeholder="Enter title"
                      value={newsData?.news_title || ""}
                      fullWidth
                      onChange={handleNewsDataChange}
                      onBlur={() => handleBlur("news_title")}
                      error={!!getVisibleError(FIELDS[0])}
                    />
                    {getVisibleError(FIELDS[0]) && (
                      <FormHelperText error>{getVisibleError(FIELDS[0])}</FormHelperText>
                    )}
                  </FormField>

                  {/* Description Input */}
                  <FormField name="description" required full>
                    <MDInput
                      name="news_description"
                      multiline
                      rows={6}
                      placeholder="Enter description"
                      fullWidth
                      value={newsData?.news_description || ""}
                      onChange={handleNewsDataChange}
                      onBlur={() => handleBlur("news_description")}
                      error={!!getVisibleError(FIELDS[1])}
                    />
                    {getVisibleError(FIELDS[1]) && (
                      <FormHelperText error>{getVisibleError(FIELDS[1])}</FormHelperText>
                    )}
                  </FormField>
                </Grid>
              </SectionCard>
            </MDBox>
          </form>

          {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
          <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
            <MDButton
              type="button"
              variant="outlined"
              color="primary"
              sx={ranuOutlinedButtonSx}
              disabled={loading}
              onClick={() => navigate("/news")}
            >
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              type="submit"
              form={FORM_ID}
              sx={ranuButtonSx}
              disabled={loading}
            >
              Save News
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default EditNews;
